(() => {
  'use strict';

  /* Time section card animation - TIME-CARDS-MOTION-2026-09-12.md (Figma
     motion study 4058:1186). Plays once per section, per card, when
     scrolled into view. All the actual timing lives in sections/time.
     liquid's stylesheet as transition-delay values keyed off .is-in; this
     file only decides WHETHER motion runs, WHEN each card's .is-in gets
     added, and drives the two count-up numbers.

     Never invisible by default (CLAUDE.md): the section's resting CSS
     (no .js-motion) is already the finished state. .js-motion is only
     ever added below, and only after the kill switches are checked -
     if it never runs, nothing was hidden in the first place. */

  const DESKTOP_QUERY = '(min-width: 990px)';
  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const CARD_STAGGER = [0, 0.18, 0.36]; // seconds, desktop only - matches --card-i in CSS (v2)
  const ROOT_MARGIN = '0px 0px -12% 0px'; // v2: trigger once cards are properly on screen, not as they clip the bottom edge

  document.querySelectorAll('.time-section').forEach(initSection);

  function initSection(section) {
    const scrollRevealOn = document.documentElement.dataset.motionScrollReveal === 'true';
    const hasObserver = 'IntersectionObserver' in window;

    if (REDUCED_MOTION || !scrollRevealOn || !hasObserver) return;

    const ring = section.querySelector('[data-motion-ring]');
    const bars = section.querySelector('[data-motion-bars]');
    const lifetime = section.querySelector('[data-motion-lifetime]');
    const cards = [ring, bars, lifetime].filter(Boolean);
    if (!cards.length) return;

    const countUpOn = section.dataset.motionCountUp === 'true';
    const isDesktop = () => window.matchMedia(DESKTOP_QUERY).matches;

    section.classList.add('js-motion');

    const trigger = (card, index) => {
      if (card.dataset.motionTriggered) return;
      card.dataset.motionTriggered = 'true';
      card.classList.add('is-in');
      // will-change is added (in CSS) when each beat starts; clear it off
      // whichever element just finished, rather than leaving it applied
      // for the life of the page.
      card.addEventListener('transitionend', (event) => {
        event.target.style.willChange = 'auto';
      });
      if (!countUpOn) return;

      const offset = isDesktop() ? 0 : CARD_STAGGER[index];
      if (card === ring) {
        scheduleCountUp(ring, 0.35 - offset, 1.5);
      } else if (card === lifetime) {
        scheduleCountUp(lifetime, 1.6 - offset, 0.8);
      }
    };

    const row = section.querySelector('.time-section__scales') || section;

    if (isDesktop()) {
      const rowObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          rowObserver.disconnect();
          cards.forEach((card, index) => trigger(card, index));
        });
      }, { threshold: 0.35, rootMargin: ROOT_MARGIN });
      rowObserver.observe(row);
    } else {
      // Below 990 the cards stack, so each is observed (and runs its own
      // beats) independently - a stagger you can't see is a delay for
      // nothing.
      cards.forEach((card, index) => {
        const cardObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            cardObserver.disconnect();
            trigger(card, index);
          });
        }, { threshold: 0.35, rootMargin: ROOT_MARGIN });
        cardObserver.observe(card);
      });
    }

    // Backstop: a failed/never-firing observer can't leave the section
    // permanently mid-animation. Reveals instantly (no.-anim, see the
    // stylesheet) rather than running the normal timed reveal - by the
    // time this fires the observer is genuinely broken, not just running
    // a little late, so there's no beat sequence worth playing catch-up
    // on. 6s (not 2s) so it only ever catches that genuine failure, not
    // a card that's merely still scrolling into view - the v2 timing
    // alone takes ~2.9s once triggered.
    const revealInstant = (card) => {
      if (card.dataset.motionTriggered) return;
      card.dataset.motionTriggered = 'true';
      card.classList.add('no-anim');
      card.classList.add('is-in');
    };

    setTimeout(() => {
      cards.forEach((card) => revealInstant(card));
    }, 6000);

    // TEMPORARY - TIME-CARDS-MOTION-2026-09-12.md v2 §"Review switch".
    // ?motion=replay makes each card clickable to reset and replay its
    // own beats, for reviewing timing without reloading and re-scrolling.
    // Remove this block before publish day.
    if (new URLSearchParams(window.location.search).get('motion') === 'replay') {
      cards.forEach((card, index) => {
        card.style.cursor = 'pointer';
        card.addEventListener('click', (event) => {
          if (event.target.closest('a')) return; // let the real CTA link work
          replayCard(card, index);
        });
      });
    }

    function replayCard(card, index) {
      card.classList.remove('is-in');
      delete card.dataset.motionTriggered;
      // Drop any count-up leftovers from the last run so they don't pile up.
      card.querySelectorAll('.visually-hidden[aria-live="off"]').forEach((el) => el.remove());
      const countTarget = card.querySelector('[data-motion-count]');
      if (countTarget) countTarget.removeAttribute('aria-hidden');
      void card.offsetWidth; // force a reflow so the removal above actually applies before re-triggering
      trigger(card, index);
    }
  }

  function scheduleCountUp(card, delaySeconds, durationSeconds) {
    const target = card.querySelector('[data-motion-count]');
    if (!target) return;
    setTimeout(() => runCountUp(target, durationSeconds), Math.max(0, delaySeconds) * 1000);
  }

  function runCountUp(el, durationSeconds) {
    const format = el.dataset.motionCount;
    const raw = el.dataset.motionCountValue || el.textContent;
    const finalText = el.textContent;
    let toValue;

    if (format === 'clock') {
      const m = raw.match(/(\d+)\s*h\D*(\d+)\s*m/i);
      if (!m) return; // unexpected format - leave the static final text alone
      toValue = parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
    } else if (format === 'decimal') {
      toValue = parseFloat(raw);
      if (Number.isNaN(toValue)) return;
    } else {
      return;
    }

    // Screen readers get the real, final value once, statically - not 40
    // intermediate frames of a counting number.
    const srValue = document.createElement('span');
    srValue.className = 'visually-hidden';
    srValue.setAttribute('aria-live', 'off');
    srValue.textContent = finalText;
    el.insertAdjacentElement('afterend', srValue);
    el.setAttribute('aria-hidden', 'true');

    el.style.fontVariantNumeric = 'tabular-nums';

    const start = performance.now();
    const durationMs = durationSeconds * 1000;

    function frame(now) {
      const t = Math.min(1, (now - start) / durationMs);
      const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic, close enough to var(--ease) for a digit count
      const current = toValue * eased;

      el.textContent = format === 'clock' ? formatClock(Math.round(current)) : current.toFixed(1);

      if (t < 1) {
        requestAnimationFrame(frame);
      } else {
        el.textContent = finalText; // final frame is the literal setting text, so it can't drift
      }
    }

    requestAnimationFrame(frame);
  }

  function formatClock(totalMinutes) {
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return `${h}h ${m}m`;
  }
})();
